import Vue from 'vue'

import ApiService from '@/core/services/api.service'
import JwtService from '@/core/services/jwt.service'

import { LOGOUT } from './auth.module.js'
import router from '@/router'
import { eventBus } from '@/main'

// action types
export const GET_REVENUECAT_DATA = 'getRevenuecatData'
export const SWITCH_REVENUECAT_STATUS = 'switchRevenuecatStatus'
export const SET_REVENUECAT_APIKEY = 'setRevenuecatApikey'
export const REGENERATE_REVENUECAT_PUBKEY = 'regenerateRevenucecatPubkey'
export const GET_ENTITLEMENTS = 'getEntitlements'
export const GET_ENTITLEMENT = 'getEntitlement'
export const ADD_ENTITLEMENT = 'addEntitlement'
export const UPDATE_ENTITLEMENT = 'updateEntitlement'
export const DELETE_ENTITLEMENT = 'deleteEntitlement'
export const CLEAR_ALL_ENTITLEMENTS = 'clearAllEntitlements'
export const SORT_ENTITLEMENTS = 'sortEntitlements'

// mutation types
export const SAVE_REVENUECAT_STATUS = 'saveRevenuecatStatus'
export const SAVE_REVENUECAT_API_KEY = 'saveRevenuecatApikey'
export const SAVE_REVENUECAT_PUB_KEY = 'saveRevenuecatPubkey'
export const CLEAR_REVENUECAT_API_KEY = 'clearRevenuecatApikey'
export const CLEAR_REVENUECAT_PUB_KEY = 'clearRevenuecatPubkey'
export const SAVE_ENTITLEMENTS = 'saveEntitlements'
export const CLEAR_ENTITLEMENTS = 'clearEntitlements'
export const SAVE_ENTITLEMENT = 'saveEntitlement'
export const ADD_NEW_ENTITLEMENT = 'addNewEntitlement'
export const SELECT_ENTITLEMENT = 'selectEntitlement'
export const UNSELECT_ENTITLEMENT = 'unselectEntitlement'
export const REMOVE_ENTITLEMENT = 'removeEntitlement'
export const SWITCH_ENTITLEMENT_EDITABLE_STATE = 'switchEntitlementEditableState'
export const ADD_NEW_ENTITLEMENT_FIELD = 'addNewEntitlementField'
export const REMOVE_NEW_ENTITLEMENT_FIELD = 'removeNewEntitlementField'
export const SAVE_MANAGE_ENTITLEMENT_ERRORS = 'saveManageEntitlementErrors'
export const CLEAR_MANAGE_ENTITLEMENT_ERRORS = 'clearManageEntitlementErrors'

const SET_RESPONSE_STATE = 'setResponseState'
export const TOAST_REVENUECAT = 'showToast/ResellerRevenuecat'

const DEFAULT_SELECTED_ENTITLEMENT = {
  _id: null,
  entitlement: '',
  devices: '',
  editable: false,
}

// returns entitlement based on _id
function findEntitlement(state, id) {
  return state.entitlementsList.filter(item => item._id === id)[0]
}

// returns entitlement error field based on _id
function findEntitlementError(state, id) {
  return state.manageEntitlementErrors.filter(item => item._id === id)[0]
}

// back-end doesn't require all object keys that I use on front-end
function convertoForBackend(entitlement, method) {
  const income = { ... entitlement }
  const requiredKeys = {
    add: ['entitlement', 'devices'],
    update: ['devices']
  }

  Object.keys(income).forEach(key => {
    if (!requiredKeys[method].includes(key)) {
      delete income[key]
    }
  })

  return income
}

const state = {
  revenuecatIsEnabled: false,
  revenuecatApikey: '',
  revenuecatPubkey: '',

  entitlementsList: [],
  selectedEntitlement: { ...DEFAULT_SELECTED_ENTITLEMENT },
  selectedEntitlementId: null,
  manageEntitlementErrors: [],

  responses: {
    getRevenuecatData: false,
    switchRevenuecatStatus: false,
    regenerateRevenuecatPubkey: false,
    getEntitlements: false,
    getEntitlement: false,
    addEntitlement: false,
    updateEntitlement: false,
    deleteEntitlement: false,
    clearAllEntitlements: false,
    sortEntitlements: false,
  }
}

const getters = {
  revenuecatIsEnabled(state) {
    return state.revenuecatIsEnabled
  },
  revenuecatApikey(state) {
    return state.revenuecatApikey
  },
  revenuecatApikeyEmpty(state) {
    return state.revenuecatApikey.length == 0
  },
  revenuecatPubkey(state) {
    return state.revenuecatPubkey
  },
  revenuecatPubkeyEmpty(state) {
    return state.revenuecatPubkey.length == 0
  },
  entitlementsList(state) {
    return state.entitlementsList
  },
  entitlementsListLength(state) {
    return state.entitlementsList.length
  },
  entitlementsListEmpty(state) {
    return state.entitlementsList.length == 0
  },
  selectedEntitlement(state) {
    return state.selectedEntitlement
  },
  selectedEntitlementId(state) {
    return state.selectedEntitlementId
  },
  noEmptyEntitlementFields(state) {
    return state.entitlementsList.filter(i => !i._id).length === 0
  },
  isNewEntitlementField: state => id => {
    return state.entitlementsList.filter(i => !i._id).length > 0 && id === null
  },
  manageEntitlementErrors(state) {
    return state.manageEntitlementErrors
  },
  entitlementFieldInvalidState: state => (id, field) => {
    const target = findEntitlementError(state, id)
    if (target) {
      return target.errors.some(err => err.field == field) ? false : null
    }
  },
  revenuecatResponses: state => type => {
    return state.responses[type]
  }
}

const actions = {
  [GET_REVENUECAT_DATA](context, { id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'getRevenuecatData', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(`${process.env.VUE_APP_RESELLER}/${id}/integration/revenuecat`)
          .then(({ data }) => {
            context.commit(SAVE_REVENUECAT_STATUS, data.status)
            context.commit(SAVE_REVENUECAT_API_KEY, data.apikey)
            context.commit(SAVE_REVENUECAT_PUB_KEY, data.pubkey)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_REVENUECAT, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'getRevenuecatData', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [SWITCH_REVENUECAT_STATUS](context, { id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'switchRevenuecatStatus', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        const payload = state.revenuecatIsEnabled ? 'disable' : 'enable'
        ApiService.put(`${process.env.VUE_APP_RESELLER}/${id}/integration/revenuecat/${payload}`)
          .then(({ data }) => {
            eventBus.$emit(TOAST_REVENUECAT, {
              message: data.message,
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_REVENUECAT, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'switchRevenuecatStatus', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [SET_REVENUECAT_APIKEY](context, { id, apikey }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'setRevenuecatApikey', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.post(
          `${process.env.VUE_APP_RESELLER}/${id}/integration/revenuecat`,
          { apikey }
        )
          .then(({ data }) => {
            eventBus.$emit(TOAST_REVENUECAT, {
              message: 'API key set',
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_REVENUECAT, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'setRevenuecatApikey', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [REGENERATE_REVENUECAT_PUBKEY](context, { id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'regenerateRevenuecatPubkey', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.post(`${process.env.VUE_APP_RESELLER}/${id}/integration/revenuecat/pubkey`)
          .then(({ data }) => {
            context.commit(SAVE_REVENUECAT_PUB_KEY, data.pubkey)
            eventBus.$emit(TOAST_REVENUECAT, {
              message: 'Public key regenerated',
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_REVENUECAT, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'regenerateRevenuecatPubkey', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [GET_ENTITLEMENTS](context, { id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'getEntitlements', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(`${process.env.VUE_APP_RESELLER}/${id}/revenuecat/entitlements`)
          .then(({ data }) => {
            context.commit(SAVE_ENTITLEMENTS, data)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_REVENUECAT, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'getEntitlements', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [GET_ENTITLEMENT](context, { reseller_id, entitlement_id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'getEntitlement', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(`${process.env.VUE_APP_RESELLER}/${reseller_id}/revenuecat/entitlement/${entitlement_id}`)
          .then(({ data }) => {
            context.commit(SAVE_ENTITLEMENT, data)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_REVENUECAT, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'getEntitlement', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [ADD_ENTITLEMENT](context, { id, entitlement }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'addEntitlement', status: true })
        eventBus.$emit('startProgress')
        const payload = convertoForBackend(entitlement, 'add')
        ApiService.post(`${process.env.VUE_APP_RESELLER}/${id}/revenuecat/entitlement`, payload)
          .then(({ data }) => {
            eventBus.$emit(TOAST_REVENUECAT, {
              message: data.message,
              variant: 'success'
            })
            context.commit(SWITCH_ENTITLEMENT_EDITABLE_STATE, {
              entitlement: entitlement,
              status: false,
            })
            context.commit(REMOVE_NEW_ENTITLEMENT_FIELD)
            context.commit(ADD_NEW_ENTITLEMENT, data)
            eventBus.$emit(TOAST_REVENUECAT, {
              message: 'New entitlement added',
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err  => {
            const error = err.response.data
            context.commit(SAVE_MANAGE_ENTITLEMENT_ERRORS, {
              _id: entitlement._id,
              errors: error.errors
            })
            eventBus.$emit(TOAST_REVENUECAT, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'addEntitlement', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [UPDATE_ENTITLEMENT](context, { id, entitlement }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'updateEntitlement', status: true })
        eventBus.$emit('startProgress')
        const entitlement_id = entitlement._id
        const payload = convertoForBackend(entitlement, 'update')
        ApiService.patch(
          `${process.env.VUE_APP_RESELLER}/${id}/revenuecat/entitlement/${entitlement_id}`,
          payload
        )
          .then(({ data }) => {
            eventBus.$emit(TOAST_REVENUECAT, {
              message: data.message,
              variant: 'success'
            })
            context.commit(SWITCH_ENTITLEMENT_EDITABLE_STATE, {
              entitlement: entitlement,
              status: false,
            })
            context.dispatch(GET_ENTITLEMENT, {
              reseller_id: id,
              entitlement_id: entitlement._id
            })
            resolve(entitlement)
          })
          .catch(err  => {
            const error = err.response.data
            context.commit(SAVE_MANAGE_ENTITLEMENT_ERRORS, {
              _id: entitlement._id,
              errors: error.errors
            })
            eventBus.$emit(TOAST_REVENUECAT, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'updateEntitlement', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [DELETE_ENTITLEMENT](context, { reseller_id, entitlement_id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'deleteEntitlement', status: true })
        eventBus.$emit('startProgress')
        ApiService.delete(`${process.env.VUE_APP_RESELLER}/${reseller_id}/revenuecat/entitlement/${entitlement_id}`)
          .then(({ data }) => {
            eventBus.$emit(TOAST_REVENUECAT, {
              message: data.message,
              variant: 'success'
            })
            context.commit(REMOVE_ENTITLEMENT, state.selectedEntitlementId)
            resolve('success')
          })
          .catch(err  => {
            const error = err.response.data
            eventBus.$emit(TOAST_REVENUECAT, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'deleteEntitlement', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [CLEAR_ALL_ENTITLEMENTS](context, { id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'clearAllEntitlements', status: true })
        eventBus.$emit('startProgress')
        ApiService.delete(`${process.env.VUE_APP_RESELLER}/${id}/revenuecat/entitlements`)
          .then(({ data }) => {
            eventBus.$emit(TOAST_REVENUECAT, {
              message: data.message,
              variant: 'success'
            })
            resolve('success')
          })
          .catch(err  => {
            const error = err.response.data
            eventBus.$emit(TOAST_REVENUECAT, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'clearAllEntitlements', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [SORT_ENTITLEMENTS](context, { id, order }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'sortEntitlements', status: true })
        eventBus.$emit('startProgress')
        ApiService.patch(
          `${process.env.VUE_APP_RESELLER}/${id}/revenuecat/entitlements/sort`,
          order
        )
          .then(({ data }) => {
            eventBus.$emit(TOAST_REVENUECAT, {
              message: data.message,
              variant: 'success'
            })
            resolve('success')
          })
          .catch(err  => {
            const error = err.response.data
            eventBus.$emit(TOAST_REVENUECAT, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'sortEntitlements', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
}

const mutations = {
  [SAVE_REVENUECAT_STATUS](state, status) {
    state.revenuecatIsEnabled = status
  },
  [SAVE_REVENUECAT_API_KEY](state, key) {
    state.revenuecatApikey = key
  },
  [CLEAR_REVENUECAT_API_KEY](state) {
    state.revenuecatApikey = ''
  },
  [SAVE_REVENUECAT_PUB_KEY](state, key) {
    state.revenuecatPubkey = key
  },
  [CLEAR_REVENUECAT_PUB_KEY](state) {
    state.revenuecatPubkey = ''
  },
  [SAVE_ENTITLEMENTS](state, data) {
    state.entitlementsList = data
  },
  [CLEAR_ENTITLEMENTS](state) {
    state.entitlementsList = []
  },
  [SAVE_ENTITLEMENT](state, entitlement) {
    const target = findEntitlement(state, entitlement._id)
    if (target) {
      Object.keys(entitlement).forEach(key => {
        Vue.set(target, key, entitlement[key])
      })
    }
  },
  [ADD_NEW_ENTITLEMENT](state, entitlement) {
    state.entitlementsList.push(entitlement)
  },
  [SELECT_ENTITLEMENT](state, entitlement) {
    state.selectedEntitlement = entitlement
    state.selectedEntitlementId = entitlement._id
  },
  [UNSELECT_ENTITLEMENT](state) {
    state.selectedEntitlement = { ...DEFAULT_SELECTED_ENTITLEMENT }
    state.selectedEntitlementId = null
  },
  [REMOVE_ENTITLEMENT](state, id) {
    const exclude = state.entitlementsList.filter(i => i._id !== id)
    state.entitlementsList = exclude
  },
  [SWITCH_ENTITLEMENT_EDITABLE_STATE](state, { entitlement, status }) {
    const target = findEntitlement(state, entitlement._id)

    // this function checks if user decided to switch editable mode
    // if he did, function creates a snapshot of how entitlement was before switching
    // otherwise function restores target values from that snapshot and deletes it
    function handleSnapshot(status, entitlement) {
      if (status) {
        target.snapshot = {
          entitlement: entitlement.entitlement,
          devices: entitlement.devices
        }
      } else {
        if (target.snapshot) {
          Object.keys(target.snapshot).forEach(key => {
            Vue.set(target, key, target.snapshot[key])
          })
          delete target.snapshot
        }
      }
    }

    if (target) {
      Vue.set(target, 'editable', status)
      handleSnapshot(status, entitlement)
    }
  },
  [ADD_NEW_ENTITLEMENT_FIELD](state, { server_id = undefined }) {
    const canAdd = state.entitlementsList.filter(i => !i._id).length === 0

    if (canAdd) {
      const newEntitlementTemplate = { ...DEFAULT_SELECTED_ENTITLEMENT }
      newEntitlementTemplate.editable = true
      if (server_id) {
        newEntitlementTemplate.server_id = server_id
      }
      state.entitlementsList.push(newEntitlementTemplate)
    }
  },
  [REMOVE_NEW_ENTITLEMENT_FIELD](state) {
    let actualEntitlementes = state.entitlementsList.filter(i => i._id)
    state.entitlementsList = actualEntitlementes
  },
  [SAVE_MANAGE_ENTITLEMENT_ERRORS](state, error) {
    state.manageEntitlementErrors.push(error)
  },
  [CLEAR_MANAGE_ENTITLEMENT_ERRORS](state, id) {
    const target = findEntitlementError(state, id)
    if (target) {
      state.manageEntitlementErrors = state.manageEntitlementErrors.filter(i => i._id !== id)
    }
  },
  [SET_RESPONSE_STATE](state, { type, status }) {
    state.responses[type] = status
  },
}

export default {
  state,
  actions,
  mutations,
  getters,
}
